import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';

import { SCREEN_ID_UTILITY_SETUP_OVERVIEW } from 'client/constants/screenIds';

import {
  UTILITY_TYPE_ID_INTERNET,
  UTILITY_ACTIVATION_STATUS_MISSING_PROVIDER,
  UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER,
} from 'common/utility/constants';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_GET,
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
  SR_API_ACTION_UTILITY_ACTIVATION_SEND_MISSING_PROVIDER_NOTIFICATION,
} from 'api/utilityActionTypes';

import { isUtilityActivationActive, canAccessActivation } from 'common/utility/helpers';

import { hasRoleAnonymous } from 'common/userRoles';

import {
  hasRoleAnonymous as hasRoleAnonymousUtilityUser,
  hasRoleAdmin,
  UTILITY_USER_SESSION_PROVIDER_VERIFICATION_CODE,
} from 'common/utility/userRoles';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withLocationQuery from 'client/helpers/withLocationQuery';
import withApiState from 'client/helpers/withApiState';
import { getActivationLoadErrorView } from 'client/helpers/activationLoadError';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import branch from 'client/core/branch';
import UtilityTypeActions from 'client/actions/UtilityType';
import UtilityPartnerActions from 'client/actions/UtilityPartner';
import UtilityActivationActions from 'client/actions/UtilityActivation';

import SrText from 'client/components/SrText';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrUtilitySetupPage from 'client/components/SrUtilitySetup/SrUtilitySetupPage';
import SrUtilitySetupOverview from 'client/components/SrUtilitySetup/SrUtilitySetupOverview';
import ActivationLoadError from 'client/components/ActivationLoadError/ActivationLoadError';

@withTransitionHook
@withLocationQuery
@withScreenId(SCREEN_ID_UTILITY_SETUP_OVERVIEW)
@withScrollToTop
@withApiState(({ match }) => ({
  getUtilityActivationApiState: {
    action: SR_API_ACTION_UTILITY_ACTIVATION_GET,
    queryId: match.params.utilityActivationShortId,
  },
}))
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  utilityUser: ['utilityUser'],
  utilityTypes: ['utilityTypes', 'list'],
  utilityActivation: ['utilityActivations', 'byShortId', match.params.utilityActivationShortId],
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
}))
@branch(({ utilityActivation }) => {
  if (utilityActivation && utilityActivation.utilityPartnerAddressId) {
    return {
      activationInternetProviders: [
        'utilityProviders',
        'internetPlansByPartnerAddressId',
        utilityActivation.utilityPartnerAddressId,
      ],
    };
  }
  return {};
})
class UtilitySetupOverviewPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Utility Setup | Overview',
  });

  static fetchData(match, inject) {
    return Promise.all([
      UtilityPartnerActions.getBySlug(inject, {
        slug: match.params.partnerSlug,
      }),
      UtilityActivationActions.getByShortId(inject, {
        utilityActivationShortId: match.params.utilityActivationShortId,
      }),
      UtilityTypeActions.getList(inject),
    ]).then(([, utilityActivation]) => {
      if (utilityActivation && utilityActivation.utilityPartnerAddressId) {
        return UtilityActivationActions.getInternetPlans(inject, {
          utilityPartnerAddressId: utilityActivation.utilityPartnerAddressId,
        });
      }
      return Promise.resolve();
    });
  }

  componentDidMount() {
    const { utilityActivation } = this.props;

    if (utilityActivation && !utilityActivation.firstLoadedAt) {
      this._onUtilityActivationFirstLoaded();
    }

    if (utilityActivation?.status === UTILITY_ACTIVATION_STATUS_MISSING_PROVIDER) {
      this._onUtilityActivationMissingProvider();

      this.reloadActivationInterval = setInterval(this._reloadActivation, 30000);
    }
  }

  componentDidUpdate(prevProps) {
    const { utilityActivation } = this.props;

    if (utilityActivation && !utilityActivation.firstLoadedAt) {
      this._onUtilityActivationFirstLoaded();
    }

    if (
      prevProps.utilityActivation?.status !== utilityActivation?.status &&
      utilityActivation?.status === UTILITY_ACTIVATION_STATUS_MISSING_PROVIDER
    ) {
      this._onUtilityActivationMissingProvider();
    }
  }

  componentWillUnmount() {
    if (this.reloadActivationInterval) {
      clearInterval(this.reloadActivationInterval);
    }
  }

  _reloadActivation = () => {
    const { dispatch, utilityActivation } = this.props;

    dispatch(UtilityActivationActions.getByShortId, {
      utilityActivationShortId: utilityActivation.shortId,
      forceRefresh: true,
    });
  };

  _onUtilityActivationFirstLoaded = () => {
    const { dispatch, utilityActivation, utilityUser, locationQuery } = this.props;

    if (locationQuery.notif === '0' || hasRoleAdmin(utilityUser.roles)) {
      return;
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE,
      payload: {
        id: utilityActivation.id,
        updates: {
          firstLoadedAt: new Date().toISOString(),
        },
      },
      onSuccess: () => {
        dispatch(UtilityActivationActions.getByShortId, {
          utilityActivationShortId: utilityActivation.shortId,
          forceRefresh: true,
        });
      },
    });
  };

  _onUtilityActivationMissingProvider = () => {
    const { dispatch, utilityActivation, locationQuery } = this.props;

    if (locationQuery.notif === '0') {
      return;
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_SEND_MISSING_PROVIDER_NOTIFICATION,
      payload: {
        utilityActivationId: utilityActivation.id,
      },
      onSuccess: () => {
        dispatch(UtilityActivationActions.getByShortId, {
          utilityActivationShortId: utilityActivation.shortId,
          forceRefresh: true,
        });
      },
    });
  };

  render() {
    const {
      currentUser,
      utilityUser,
      utilityTypes,
      utilityPartner,
      utilityActivation,
      activationInternetProviders = [],
      getUtilityActivationApiState,
    } = this.props;

    const { partnerSlug, utilityActivationShortId } = this.props.match.params;
    const errorView = getActivationLoadErrorView({
      apiState: getUtilityActivationApiState,
      variant: 'renter',
      backTo: `/utility-setup/${partnerSlug}/${utilityActivationShortId}`,
      backLabel: 'Back to my utilities',
    });

    if (errorView.isError && !utilityActivation) {
      if (errorView.shouldRedirectToLogin) {
        return <Redirect to={`/utility-setup/${partnerSlug}/login`} />;
      }
      return (
        <ActivationLoadError
          message={errorView.message}
          backTo={errorView.backTo}
          backLabel={errorView.backLabel}
        />
      );
    }

    if (!utilityPartner || !utilityActivation) {
      return null;
    }

    if (!utilityActivation.utilityPartnerAddressId) {
      return <Redirect to={`/utility-setup/${utilityPartner.slug}`} />;
    }

    if (!isUtilityActivationActive(utilityActivation)) {
      return <Redirect to={`/utility-setup/${utilityActivation.partner.slug}`} />;
    }

    const utilitySetupPageProps = {
      addressDisplay: utilityActivation.address?.display,
      utilityPartner,
      utilityActivationId: utilityActivation.id,
      logoUrl: utilityPartner.logoUrl,
      backButtonText: 'Logout',
      backButtonUrl: `/logout?next=/utility-setup/${utilityPartner.slug}`,
    };

    if (hasRoleAnonymousUtilityUser(utilityUser.roles)) {
      if (hasRoleAnonymous(currentUser.roles)) {
        utilitySetupPageProps.backButtonText = 'Back';
        utilitySetupPageProps.backButtonUrl = `/utility-setup/${utilityActivation.partner.slug}?noRedirect=true`;
      } else {
        utilitySetupPageProps.backButtonText = 'Back to Sunroom';
        utilitySetupPageProps.backButtonUrl = '/profile';
      }
    }

    if (!canAccessActivation({
      utilityActivation,
      utilityUser,
    }) && utilityActivation.user.sessionProvider !== UTILITY_USER_SESSION_PROVIDER_VERIFICATION_CODE) {
      return <Redirect to={`/utility-setup/${utilityActivation.partner.slug}/login/${utilityActivation.address.id}`} />;
    }

    const internetUtility = utilityActivation.items.find(
      ({ utilityTypeId }) => UTILITY_TYPE_ID_INTERNET === utilityTypeId,
    );

    const shouldShowMissingProviderUtilitiesModal =
      utilityActivation.status === UTILITY_ACTIVATION_STATUS_MISSING_PROVIDER &&
      internetUtility?.status === UTILITY_ACTIVATION_ITEM_STATUS_MISSING_PROVIDER;

    return (
      <SrUtilitySetupPage {...utilitySetupPageProps}>
        <SrUtilitySetupOverview
          utilityTypes={utilityTypes}
          utilityPartner={utilityPartner}
          utilityActivation={utilityActivation}
          utilityUser={utilityUser}
          activationInternetProviders={activationInternetProviders}
        />
        {shouldShowMissingProviderUtilitiesModal && (
          <SrSimpleMessageModal
            iconUrl="/images/loading-house.svg"
            headline="Come back in 10 minutes"
            underline
            message={
              <SrText font="inherit" weight="600">
                We are currently analyzing which utilities are available for your address.
                <br />
                <br />
                Please come back to this page soon!
              </SrText>
            }
            maxWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
      </SrUtilitySetupPage>
    );
  }
}

UtilitySetupOverviewPage.displayName = 'UtilitySetupOverviewPage';

export default UtilitySetupOverviewPage;
